import { all, call, put, takeLatest } from "redux-saga/effects";
import {
    GET_QF_DASHBOARD_STATS_V4_STATES,
    GET_QF_DASHBOARD_STATS_V4_SOURCES,
    GET_QF_DASHBOARD_STATS_V4_CREATIVES,
    GET_QF_DASHBOARD_STATS_V4_INSURANCE_TYPES,
    GET_QF_DASHBOARD_STATS_V4_OVERVIEW,
} from "./actionTypes";
import {
    getQFDashboardStatsV4StatesSuccess, getQFDashboardStatsV4StatesError,
    getQFDashboardStatsV4SourcesSuccess, getQFDashboardStatsV4SourcesError,
    getQFDashboardStatsV4CreativesSuccess, getQFDashboardStatsV4CreativesError,
    getQFDashboardStatsV4InsuranceTypesSuccess, getQFDashboardStatsV4InsuranceTypesError,
    getQFDashboardStatsV4OverviewSuccess, getQFDashboardStatsV4OverviewError,
} from "./actions";
import { genericGetRequest_2 } from "helpers/sf_backend_helper";
import { AppConfiguration } from "config";
import { getCurrentBrandFromLocalStorage } from "helpers/sf_utils";
import { DASHBOARD_STAT_TYPES } from "helpers/sf_constants";

// Common saga helper to fetch Dashboard V4 stats (reduces duplication)
function* fetchDashboardStatsV4({ action, stat_type, onSuccess, onError, paramsOverride, shouldDispatch = true }) {
    try {
        const user = JSON.parse(localStorage.getItem("authUser")).user;
        const currentBrand = getCurrentBrandFromLocalStorage();

        const payload = paramsOverride || action.payload || {};
        const params = { stat_type, ...payload };

        const response = yield call(
            genericGetRequest_2,
            `${AppConfiguration.BACKEND_URL}/api/v4/dashboard_stats/quote_funnels`,
            params,
            {
                Authorization: user.token,
                "account-id": user.account_id,
                "brand-id": currentBrand?.id,
            }
        );

        if (shouldDispatch && onSuccess) {
            yield put(onSuccess(response));
        }
        return response;
    } catch (error) {
        if (shouldDispatch && onError) {
            yield put(onError(error));
        }
    }
}

// Quote Funnels - States
function* onGetQFDashboardStatsV4States(action) {
    yield* fetchDashboardStatsV4({
        action,
        stat_type: DASHBOARD_STAT_TYPES.STATES,
        onSuccess: getQFDashboardStatsV4StatesSuccess,
        onError: getQFDashboardStatsV4StatesError,
    });
}

// Quote Funnels - Sources
function* onGetQFDashboardStatsV4Sources(action) {
    yield* fetchDashboardStatsV4({
        action,
        stat_type: DASHBOARD_STAT_TYPES.SOURCES,
        onSuccess: getQFDashboardStatsV4SourcesSuccess,
        onError: getQFDashboardStatsV4SourcesError,
    });
}

// Quote Funnels - Creatives
function* onGetQFDashboardStatsV4Creatives(action) {
    yield* fetchDashboardStatsV4({
        action,
        stat_type: DASHBOARD_STAT_TYPES.CREATIVES,
        onSuccess: getQFDashboardStatsV4CreativesSuccess,
        onError: getQFDashboardStatsV4CreativesError,
    });
}

// Quote Funnels - Insurance Types
function* onGetQFDashboardStatsV4InsuranceTypes(action) {
    yield* fetchDashboardStatsV4({
        action,
        stat_type: DASHBOARD_STAT_TYPES.INSURANCE_TYPES,
        onSuccess: getQFDashboardStatsV4InsuranceTypesSuccess,
        onError: getQFDashboardStatsV4InsuranceTypesError,
    });
}

// Quote Funnels - Overview (total_stats + graph_stats)
function* onGetQFDashboardStatsV4Overview(action) {
    try {
        const { prev_start_date, prev_end_date, ...basePayload } = action.payload || {};
        const currentCall = call(fetchDashboardStatsV4, {
            action,
            stat_type: DASHBOARD_STAT_TYPES.OVERVIEW,
            paramsOverride: basePayload,
            shouldDispatch: false,
        });

        const prevCall = (prev_start_date && prev_end_date) ? call(fetchDashboardStatsV4, {
            action,
            stat_type: DASHBOARD_STAT_TYPES.OVERVIEW,
            paramsOverride: { ...basePayload, start_date: prev_start_date, end_date: prev_end_date },
            shouldDispatch: false,
        }) : null;

        const [currentResp, prevResp] = yield all([currentCall, prevCall]);

        yield put(getQFDashboardStatsV4OverviewSuccess({
            overview_total_stats: currentResp?.data?.total_stats || null,
            overview_graph_stats: currentResp?.data?.graph_stats || [],
            overview_prev_total_stats: prevResp?.data?.total_stats || null,
            overview_prev_graph_stats: prevResp?.data?.graph_stats || [],
        }));
    } catch (error) {
        yield put(getQFDashboardStatsV4OverviewError(error));
    }
}

function* dashboardV4Saga() {
    yield takeLatest(GET_QF_DASHBOARD_STATS_V4_STATES, onGetQFDashboardStatsV4States);
    yield takeLatest(GET_QF_DASHBOARD_STATS_V4_SOURCES, onGetQFDashboardStatsV4Sources);
    yield takeLatest(GET_QF_DASHBOARD_STATS_V4_CREATIVES, onGetQFDashboardStatsV4Creatives);
    yield takeLatest(GET_QF_DASHBOARD_STATS_V4_INSURANCE_TYPES, onGetQFDashboardStatsV4InsuranceTypes);
    yield takeLatest(GET_QF_DASHBOARD_STATS_V4_OVERVIEW, onGetQFDashboardStatsV4Overview);
}

export default dashboardV4Saga;
